import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { MoreHorizontal, Plus, ExternalLink, Pencil, Archive, Trash2, Search, Lock } from "lucide-react";
import { statusMeta, formatDate } from "../lib/format";
import DeleteProjectDialog from "../components/DeleteProjectDialog";
import ProjectFormDialog from "../components/ProjectFormDialog";
import { PrintButton, PrintHeader } from "../components/Print";

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formTarget, setFormTarget] = useState(null); // undefined=hidden, null=new, project=edit
  const [formOpen, setFormOpen] = useState(false);
  const { canDelete } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects", { params: { archived: false } });
      setProjects(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.client || "").toLowerCase().includes(q) ||
        (p.location || "").toLowerCase().includes(q) ||
        (p.status || "").toLowerCase().includes(q)
    );
  }, [projects, query]);

  const doArchive = async (project) => {
    await api.post(`/projects/${project.id}/archive`);
    toast.success(`Archived "${project.name}"`);
    load();
  };

  const doDelete = async (project) => {
    try {
      const res = await api.delete(`/projects/${project.id}`);
      toast.success(
        `Deleted "${project.name}"${res.data.deleted_transactions ? ` (${res.data.deleted_transactions} transactions moved to audit log)` : ""}`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
      throw e;
    }
  };

  const submitForm = async (data) => {
    if (formTarget) {
      const patch = { name: data.name, client: data.client, location: data.location, status: data.status, start_date: data.start_date || null, end_date: data.end_date || null, notes: data.notes, assignees: data.assignees || [], crew_size: Number(data.crew_size) || 0 };
      await api.patch(`/projects/${formTarget.id}`, patch);
      toast.success("Project updated");
    } else {
      await api.post("/projects", data);
      toast.success("Project created");
    }
    load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Manage"
        title="Projects"
        description="Every project has row-level Open, Edit, Archive and Delete actions. No side-trips required."
        testId="projects-header"
        actions={
          <div className="flex items-center gap-2 no-print">
            <PrintButton testId="projects-print-btn" />
            <Button
              onClick={() => { setFormTarget(null); setFormOpen(true); }}
              data-testid="new-project-btn"
              className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
            >
              <Plus size={16} className="mr-1.5" /> New Project
            </Button>
          </div>
        }
      />

      <div className="p-4 md:p-8 print-container">
        <PrintHeader title="Projects" subtitle={`${filtered.length} of ${projects.length} shown`} />
        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search projects, clients, locations…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="projects-search-input"
              className="pl-9 rounded-sm border-slate-300"
            />
          </div>
          <div className="text-xs font-mono text-slate-500" data-testid="projects-count">
            {filtered.length} / {projects.length} shown
          </div>
        </div>

        <div className="border border-slate-200 rounded-md overflow-x-auto bg-white shadow-sm">
          <table className="w-full text-sm min-w-[720px]" data-testid="projects-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="p-4">Project</th>
                <th className="p-4">Client</th>
                <th className="p-4">Location</th>
                <th className="p-4">Status</th>
                <th className="p-4">Updated</th>
                <th className="p-4 w-16 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-4 md:p-8 text-center text-slate-500 font-mono text-sm">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center">
                    <div className="text-slate-500 font-mono text-sm">No projects match your search.</div>
                  </td>
                </tr>
              ) : (
                filtered.map((p) => {
                  const meta = statusMeta(p.status, p.archived);
                  return (
                    <tr key={p.id} className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors" data-testid={`project-row-${p.id}`}>
                      <td className="p-4">
                        <button
                          onClick={() => navigate(`/projects/${p.id}`)}
                          className="text-slate-900 font-semibold hover:text-blue-800 hover:underline text-left"
                          data-testid={`project-name-${p.id}`}
                        >
                          {p.name}
                        </button>
                      </td>
                      <td className="p-4 text-slate-700">{p.client || "—"}</td>
                      <td className="p-4 text-slate-700">{p.location || "—"}</td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-semibold ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="p-4 text-slate-500 font-mono text-xs">{formatDate(p.updated_at)}</td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Project actions"
                              data-testid={`project-actions-${p.id}`}
                              className="h-8 w-8 rounded-sm hover:bg-slate-100"
                            >
                              <MoreHorizontal size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48 rounded-sm">
                            <DropdownMenuLabel className="text-xs uppercase tracking-widest text-slate-500">
                              Actions
                            </DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => navigate(`/projects/${p.id}`)}
                              data-testid={`project-open-${p.id}`}
                            >
                              <ExternalLink size={14} className="mr-2" /> Open
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => { setFormTarget(p); setFormOpen(true); }}
                              data-testid={`project-edit-${p.id}`}
                            >
                              <Pencil size={14} className="mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => doArchive(p)}
                              data-testid={`project-archive-${p.id}`}
                            >
                              <Archive size={14} className="mr-2" /> Archive
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {canDelete ? (
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(p)}
                                className="text-red-600 focus:text-red-700 focus:bg-red-50"
                                data-testid={`project-delete-${p.id}`}
                              >
                                <Trash2 size={14} className="mr-2" /> Delete
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem disabled data-testid={`project-delete-disabled-${p.id}`}>
                                <Lock size={14} className="mr-2" /> Delete (Admin only)
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteProjectDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        project={deleteTarget}
        onArchive={() => doArchive(deleteTarget)}
        onDelete={() => doDelete(deleteTarget)}
      />

      <ProjectFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        project={formTarget}
        onSubmit={submitForm}
      />
    </div>
  );
}
